import React from 'react';
import {Typography} from '@mui/material';
import {ActionType} from '../../components/AutoForm/AutoFieldNew';
import {WizardField} from '../../components/Wizard/QuestionWrapper';
import {ContactAttribution} from '../PrecursorSummaryPage/headerFields';
import {postcursorFieldsType} from './postcursorFields';
import {AccountDetailsFragment} from '../../schema-types/AccountDetailsFragment';
import {ContactBasicFragment} from '../../schema-types/ContactBasicFragment';

export const investmentPhilosophyFields = (
  account: AccountDetailsFragment,
  canUseRisk: boolean
): WizardField<postcursorFieldsType>[] => {
  /**
   * This actually could be for more contacts
   * we should be asking this question for all contacts on the account
   * not just primary and secondary
   */
  const contacts: (AccountDetailsFragment | ContactBasicFragment)[] = [];

  if (account.isTrust) {
    contacts.push(account);
  } else if (canUseRisk) {
    // dont show ULN if we are using risk, risk contains ULN
  } else {
    // For household accounts, we only have primary and secondary contacts
    if (account.primaryContact) {
      contacts.push(account.primaryContact);
    }
    if (account.secondaryContact) {
      contacts.push(account.secondaryContact);
    }
  }

  const precursorFields: WizardField<postcursorFieldsType>[][] = contacts.map((c) => {
    const contactId = c?.id;
    const name = c.__typename === 'AccountType' ? account.name : c.simplifiedName;
    const accountHolderType = account.isTrust ? 'trust' : 'client';

    return [
      {
        id: `giaHasLiquidityNeeds.${contactId}` as any,
        title: `Does ${name} have GIA liquidity needs`,
        label: '',
        question: `${name}'s GIA liquidity needs`,
        questionComponent: (
          <div>
            <Typography>
              Private assets can take longer to sell than listed stocks. Clients should
              only hold these assets if they are unlikely to need access to a significant
              proportion of the money at short notice. This should be assessed after
              taking into consideration their emergency funds and other money that remains
              accessible. A significant proportion is considered to be 25% or more and
              short notice is defined as 4 months from request to payment.
            </Typography>
            <Typography mt={2}>
              <strong>
                Does the {accountHolderType} foresee needing more than 25% of their GIA at
                short notice (&lt;4 months)?
              </strong>
            </Typography>
          </div>
        ),
        enabled: (values) => {
          if (
            values.reviewContact !== ContactAttribution.both &&
            values.reviewContact !== contactId &&
            !account.isTrust
          ) {
            return false;
          }
          return true; // Always show this question
        },
        type: ActionType.buttonGroup,
        options: [
          {label: 'Yes', value: true},
          {label: 'No', value: false},
        ],
        yesValue: true,
        noValue: false,
        requireSubmit: true,
      },
      {
        id: `pensionHasLiquidityNeeds.${contactId}` as any,
        title: `Does ${name} have Pension liquidity needs`,
        label: '',
        question: `${name}'s Pension liquidity needs`,
        questionComponent: (
          <div>
            <Typography>
              Private assets can take longer to sell than listed stocks. Clients should
              only hold these assets if they are unlikely to need access to a significant
              proportion of the money at short notice. This should be assessed after
              taking into consideration their emergency funds and other money that remains
              accessible. A significant proportion is considered to be 25% or more and
              short notice is defined as 4 months from request to payment.
            </Typography>
            <Typography mt={2}>
              <strong>
                Does the {accountHolderType} foresee needing more than 25% of their
                Pension at short notice (&lt;4 months)?
              </strong>
            </Typography>
          </div>
        ),
        enabled: (values) => {
          if (
            values.reviewContact !== ContactAttribution.both &&
            values.reviewContact !== contactId &&
            !account.isTrust
          ) {
            return false;
          }
          return true; // Always show this question
        },
        type: ActionType.buttonGroup,
        options: [
          {
            label: 'Yes',
            value: true,
          },
          {
            label: 'No',
            value: false,
          },
        ],
        yesValue: true,
        noValue: false,
        requireSubmit: true,
      },
    ];
  }) || [[]];
  return precursorFields.flat(1);
};
